/**
 * GroupSeatManagement Repository
 * 그룹 좌석 관리 API 호출
 */
import { API_BASE_URL } from '../../../config/api';
import { SeatGroup, AvailableSeat, GroupFormData } from '../models/GroupSeatManagement';

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken') || 'demo-token';
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

export class GroupSeatManagementRepository {
  /**
   * 그룹 목록 조회
   */
  async getGroups(): Promise<SeatGroup[]> {
    const response = await fetch(`${API_BASE_URL}/v1/seat-config/groups`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 사용 가능한 좌석 조회
   */
  async getAvailableSeats(): Promise<AvailableSeat[]> {
    const response = await fetch(`${API_BASE_URL}/v1/seat-config/available-seats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 그룹 생성
   */
  async createGroup(data: GroupFormData): Promise<SeatGroup> {
    const response = await fetch(`${API_BASE_URL}/v1/seat-config/groups`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 그룹 수정
   */
  async updateGroup(groupId: string, data: Partial<GroupFormData>): Promise<SeatGroup> {
    const response = await fetch(`${API_BASE_URL}/v1/seat-config/groups/${groupId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 그룹 삭제
   */
  async deleteGroup(groupId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/v1/seat-config/groups/${groupId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
  }

  /**
   * 그룹 활성화/비활성화
   */
  async toggleGroupActive(groupId: string, isActive: boolean): Promise<SeatGroup> {
    const response = await fetch(
      `${API_BASE_URL}/v1/seat-config/groups/${groupId}/toggle-active`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: isActive }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  }
}

export const groupSeatManagementRepository = new GroupSeatManagementRepository();
